import { AlertTriangle, ExternalLink } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  isInAppBrowser,
  getInAppBrowserName,
  getOpenInBrowserInstructions,
} from '@/utils/browserDetection';

interface InAppBrowserWarningProps {
  /**
   * If true, show the warning even if not in an in-app browser (for error recovery)
   */
  forceShow?: boolean;
}

export function InAppBrowserWarning({
  forceShow = false,
}: InAppBrowserWarningProps) {
  const shouldShow = forceShow || isInAppBrowser();

  if (!shouldShow) {
    return null;
  }

  const browserName = getInAppBrowserName();
  const instructions = getOpenInBrowserInstructions();
  const currentUrl = window.location.href;

  const copyUrlToClipboard = () => {
    navigator.clipboard.writeText(currentUrl).catch(err => {
      console.error('Failed to copy URL:', err);
    });
  };

  return (
    <Alert className="mb-6 border-orange-200 bg-orange-50">
      <AlertTriangle className="h-5 w-5 text-orange-600" />
      <AlertTitle className="text-orange-900 font-semibold mb-2">
        {forceShow
          ? 'Browser Compatibility Issue'
          : `${browserName} Browser Not Supported`}
      </AlertTitle>
      <AlertDescription className="text-orange-800 space-y-3">
        <p className="text-sm">
          {forceShow
            ? `We've detected a compatibility issue with your current browser. This often happens when using ${browserName}'s in-app browser.`
            : `For the best experience and to avoid authentication issues, please open this page in your phone's default browser.`}
        </p>

        <div className="text-sm space-y-2">
          <p className="font-semibold">
            How to open in{' '}
            {instructions.platform === 'iOS' ? 'Safari' : 'Chrome'}:
          </p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            {instructions.steps.map((step, index) => (
              <li key={index}>{step}</li>
            ))}
          </ol>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <Button
            onClick={copyUrlToClipboard}
            variant="outline"
            size="sm"
            className="text-orange-900 border-orange-300 hover:bg-orange-100"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            Copy Link
          </Button>
          <Button
            onClick={() => {
              // Try to open in default browser (works on some platforms)
              window.open(currentUrl, '_system');
            }}
            variant="outline"
            size="sm"
            className="text-orange-900 border-orange-300 hover:bg-orange-100"
          >
            Open in Browser
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
